import React, { useCallback } from "react";
import { User } from "@supabase/supabase-js";
import { ProfileData } from "@/hooks/useProfiles";
import { toast } from "sonner";
import { useProfileContext } from "@/contexts/ProfileContext";
import { supabase } from "@/integrations/supabase/client";
import ProfileForm from "../ProfileForm";
import AboutMeSection from "../profile/about-me/AboutMeSection";
import PortfolioSection from "../profile/PortfolioSection";
import ExperienceSection from "../profile/ExperienceSection";
import { showErrorToast } from "@/utils/toastUtils";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Json } from "@/services/profiles/types";

// Use ProfileData as Profile for backward compatibility
type Profile = ProfileData;

// Types
type SectionType = "profile" | "about" | "portfolio" | "experience";

interface ProfileSectionHandlerProps {
  type: SectionType;
  user: User;
  profile: Profile | null;
  isLoading?: boolean; // Add loading prop
}

interface PortfolioItem {
  id: string;
  title: string;
  description: string;
  image_url?: string;
  images?: Array<{
    id: string;
    url: string;
    order: number;
    caption: string;
  }>;
  created_at: string;
}

// Avatar uploads are now handled immediately, so no need for file/blob data

// Skeleton loaders
const ProfileSkeleton = () => (
  <div className="space-y-4">
    <Skeleton className="h-10 w-1/3" />
    <Skeleton className="h-40 w-full" />
    <div className="space-y-2">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
    </div>
  </div>
);

const AboutSkeleton = () => (
  <div className="space-y-6">
    <Skeleton className="h-10 w-1/3" />
    <div className="space-y-2">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
    <div className="space-y-2">
      <Skeleton className="h-10 w-1/2" />
      <Skeleton className="h-10 w-full" />
    </div>
  </div>
);

const PortfolioSkeleton = () => (
  <div className="space-y-6">
    {/* Header skeleton */}
    <div className="flex items-center justify-between">
      <Skeleton className="h-8 w-1/4" />
      <div className="flex gap-2">
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-8 w-24" />
      </div>
    </div>
    
    {/* Portfolio grid skeleton */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="space-y-3">
          <Skeleton className="aspect-square w-full rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
    
    {/* Case studies section skeleton */}
    <div className="space-y-4">
      <Skeleton className="h-6 w-1/4" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="space-y-3">
            <Skeleton className="aspect-square w-full rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ExperienceSkeleton = () => (
  <div className="space-y-6">
    <Skeleton className="h-10 w-1/3" />
    <div className="space-y-4">
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  </div>
);

const ProfileSectionHandler: React.FC<ProfileSectionHandlerProps> = ({
  type,
  user,
  profile,
  isLoading = false,
}) => {
  const { updateProfile } = useProfileContext();

  // Handle about me section updates
  const handleAboutMeUpdate = useCallback(
    async (updatedData: Partial<Profile>) => {
      try {
        // Avatar is already uploaded and URL is included in updatedData
        // Use context updateProfile instead of direct database update
        updateProfile(updatedData);

        toast.success(
          "Your profile information has been updated successfully."
        );
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "An unexpected error occurred";
        toast.error(errorMessage);
      }
    },
    [updateProfile]
  );

  // Handle portfolio updates
  const handlePortfolioUpdate = useCallback(
    async (portfolioItems: PortfolioItem[]) => {
      try {
        // Use context updateProfile
        updateProfile({
          portfolio_items: portfolioItems as unknown as Json[],
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "An unexpected error occurred";
        toast.error(errorMessage);
      }
    },
    [updateProfile, toast]
  );

  // Handle experience updates
  const handleExperienceUpdate = useCallback(
    async (items: Profile["experience_items"]) => {
      try {
        // Use context updateProfile
        updateProfile({
          experience_items: items as unknown as Json[],
        });

        toast.success("Your experience has been updated successfully.");
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "An unexpected error occurred";
        showErrorToast("Error", errorMessage);
      }
    },
    [updateProfile, toast]
  );


  // Show loading state if profile is missing
  // Portfolio section handles its own loading state
  if (!profile?.id && type !== "portfolio") {
    switch (type) {
      case "profile":
        return <ProfileSkeleton />;
      case "about":
        return <AboutSkeleton />;
      case "portfolio":
        return <PortfolioSkeleton />;
      case "experience":
        return <ExperienceSkeleton />;
      default:
        return <div>Loading...</div>;
    }
  }

  // Ensure profile data is properly initialized (only after null check)
  const safeProfile = {
    ...profile,
    bio: profile?.bio || null,
    location: profile?.location || null,
    profession: profile?.profession || null,
    skills: profile?.skills || [],
    languages: profile?.languages || [],
    education: profile?.education || null,
    years_experience: profile?.years_experience || null,
    avatar_url: profile?.avatar_url || null,
    display_name: profile?.display_name || "",
    username: profile?.username || "",
    portfolio_items: profile?.portfolio_items || [],
    experience_items: profile?.experience_items || [],
  };

  // Render appropriate section
  switch (type) {
    case "profile":
      return (
        <ProfileForm
          user={user}
          initialProfile={safeProfile}
          onProfileUpdate={updateProfile}
        />
      );
    case "about":
      return (
        <AboutMeSection
          user={user}
          {...safeProfile}
          onAboutMeChange={handleAboutMeUpdate}
          activeSection="about"
        />
      );
    case "portfolio":
      return (
        <PortfolioSection
          portfolioItems={[]} // Portfolio items handled by unified query
          onPortfolioChange={handlePortfolioUpdate}
          activeSection="portfolio"
          userProfession={safeProfile.profession}
          userSkills={safeProfile.skills}
          isVerified={!!safeProfile.is_featured}
        />
      );
    case "experience":
      return (
        <ExperienceSection
          experienceItems={safeProfile.experience_items}
          onExperienceChange={handleExperienceUpdate}
          user={user}
          activeSection="experience"
        />
      );
    default:
      return (
        <Alert>
          <AlertDescription>Unknown section type: {type}</AlertDescription>
        </Alert>
      );
  }
};

export default ProfileSectionHandler;
